import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as z from 'zod';

import { selectUser } from '../../features/auth/authSlice';
import { useGetGlobalQuery } from '../../features/my-certifications/myCertificationsApiSlice';
import { Certification } from '../../types/certifications/Certification';
import { ConductRulesSection } from './ConductRulesSection';

function getVimeoEmbedUrl(url: string): string | null {
  const match = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (!match) return null;
  return `https://player.vimeo.com/video/${match[1]}`;
}

type PropsRequirements = {
  certification?: Certification;
  onAccept: () => void;
  onBack?: () => void;
  stepLabel?: string;
};

const RequirementsSchema = z.object({
  accept: z.boolean().refine(value => value, {
    message: 'Você precisa aceitar os termos para continuar.',
  }),
});

type RequirementsForm = z.infer<typeof RequirementsSchema>;

export function Requirements({
  certification,
  onAccept,
  onBack,
  stepLabel = 'Etapa 1 de 2',
}: PropsRequirements) {
  const user = useSelector(selectUser);
  const [regulationExpanded, setRegulationExpanded] = useState(false);

  const { data } = useGetGlobalQuery(
    { apiKey: user?.apiKey },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RequirementsForm>({
    mode: 'onSubmit',
    resolver: zodResolver(RequirementsSchema),
    defaultValues: {
      accept: false,
    },
  });

  const supportLink = useMemo(
    () => data?.regulation?.url,
    [data?.regulation?.url],
  );
  const vimeoEmbedUrl = useMemo(
    () =>
      certification?.exam_how_it_works_video_url
        ? getVimeoEmbedUrl(certification.exam_how_it_works_video_url)
        : null,
    [certification?.exam_how_it_works_video_url],
  );
  const hasAcceptedTerms = watch('accept');
  const isFiscalMonitoring = certification?.monitoring === 'fiscal';

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-5xl rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="flex flex-col gap-6 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              {stepLabel}
            </p>
            <h1 className="mt-3 text-3xl font-bold text-slate-900">
              Requisitos e regras da prova
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              {(() => {
                if (isFiscalMonitoring) {
                  return 'Revise o resumo executivo e leia o regulamento completo antes do aceite.';
                }
                if (vimeoEmbedUrl) {
                  return (
                    <>
                      Atenção: Antes de prosseguir, revise as regras,{' '}
                      <strong>leia o regulamento e assista ao vídeo.</strong> O
                      descumprimento pode levar ao bloqueio ou encerramento da
                      prova.
                    </>
                  );
                }
                return 'Revise o resumo executivo e leia o regulamento completo antes do aceite. Esta etapa reúne as regras que podem bloquear ou encerrar a prova.';
              })()}
            </p>
          </div>

          <div className="rounded-2xl bg-slate-900 p-5 text-white">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
              Resumo executivo
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-sm text-slate-300">Duração</p>
                <p className="text-lg font-semibold">
                  {certification?.quiz_duration ?? '-'} minutos
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-300">Questões</p>
                <p className="text-lg font-semibold">
                  {certification?.total_questions ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-300">Nota mínima</p>
                <p className="text-lg font-semibold">70%</p>
              </div>
              <div>
                <p className="text-sm text-slate-300">Suporte</p>
                <p className="text-lg font-semibold">
                  {supportLink
                    ? 'Disponível no regulamento'
                    : 'Contato interno'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <ConductRulesSection />

            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                Ambiente recomendado
              </p>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                <li className="rounded-xl border border-slate-200 px-4 py-3">
                  {isFiscalMonitoring
                    ? 'Computador desktop ou notebook e conexão estável.'
                    : 'Computador desktop ou notebook, webcam física, microfone e conexão estável.'}
                </li>
                <li className="rounded-xl border border-slate-200 px-4 py-3">
                  Navegador compatível, JavaScript e cookies habilitados.
                </li>
                <li className="rounded-xl border border-slate-200 px-4 py-3">
                  Use um único monitor e mantenha resolução adequada para
                  leitura.
                </li>
              </ul>
            </section>
          </div>

          <div className="space-y-5">
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                Candidato
              </p>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <div className="rounded-xl border border-slate-200 px-4 py-3">
                  <strong className="block text-slate-900">Nome</strong>
                  {user?.name}
                </div>
                <div className="rounded-xl border border-slate-200 px-4 py-3">
                  <strong className="block text-slate-900">Prova</strong>
                  {certification?.title}
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-blue-200 bg-blue-50/50 p-5">
              <div className="mb-3 inline-flex rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-blue-800">
                Leitura obrigatória
              </div>
              <button
                type="button"
                onClick={() =>
                  setRegulationExpanded(currentState => !currentState)
                }
                className={`flex w-full items-center justify-between text-left ${
                  regulationExpanded ? '' : 'cta-guidance cta-guidance-blue'
                }`}
              >
                <div>
                  <p className="text-sm font-semibold text-blue-700  uppercase tracking-[0.2em]">
                    {regulationExpanded
                      ? 'Ocultar regulamento'
                      : 'Ler regulamento'}
                  </p>
                  <p className="mt-1 text-sm text-slate-700">
                    Leia o texto integral antes de concluir o aceite dos termos.
                  </p>
                </div>
              </button>

              {regulationExpanded && (
                <div className="mt-4 space-y-4">
                  {data?.regulation_text && (
                    <textarea
                      className="min-h-[220px] w-full rounded-xl border-slate-300 bg-white text-sm"
                      defaultValue={data.regulation_text}
                      readOnly
                    />
                  )}

                  {supportLink && (
                    <a
                      className="inline-flex text-sm font-semibold text-blue-700 underline"
                      href={supportLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Abrir regulamento em nova aba
                    </a>
                  )}
                </div>
              )}

              {vimeoEmbedUrl && (
                <div className="mt-4">
                  <p className="mb-2 text-sm font-semibold text-blue-700 uppercase tracking-[0.2em]">
                    Vídeo Para realização da prova
                  </p>
                  <div className="relative w-full overflow-hidden rounded-xl border border-blue-200 bg-black aspect-video">
                    <iframe
                      src={vimeoEmbedUrl}
                      title="Guia de como fazer a prova"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 h-full w-full"
                    />
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>

        <form
          className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5"
          onSubmit={handleSubmit(onAccept)}
        >
          <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700">
            <input
              {...register('accept')}
              className="mt-1 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
              id="accept"
              type="checkbox"
            />
            <span>
              Eu, <strong className="uppercase">{user?.name}</strong>, declaro
              que li
              {vimeoEmbedUrl
                ? ', assisti ao vídeo explicativo para a realização da prova'
                : ''}{' '}
              e aceito os requisitos, condutas proibidas e termos do regulamento
              para realizar esta prova.
            </span>
          </label>

          <p className="mt-3 text-xs text-slate-500">
            Recomendação: leia o regulamento completo antes de marcar o aceite.
          </p>

          {errors.accept && (
            <p className="mt-3 text-sm font-medium text-red-700">
              {errors.accept.message}
            </p>
          )}

          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={onBack}
              className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Voltar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`rounded-xl bg-blue-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:opacity-60 ${
                hasAcceptedTerms && !isSubmitting
                  ? 'cta-guidance cta-guidance-blue'
                  : ''
              }`}
            >
              {isSubmitting
                ? 'Validando aceite...'
                : 'Aceitar e seguir para preparação técnica'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
